import {
  Typography,
  Paper,
  Box,
  TableRow,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableBody,
  Button,
  TextField,
  Dialog,
  DialogContent,
  MenuItem,
  IconButton,
  DialogActions,
} from "@mui/material"
import { Fragment, useEffect, useState } from "react"
import axios from "axios"
import DeleteIcon from "@mui/icons-material/Delete"
import MapIcon from "@mui/icons-material/Map"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import { useKeycloak } from "../auth/keycloakContext"
import Header from "./Header"
import HandleFail from "../utils/error_handler"

const SharedWith = ({ session_id }) => {
  const [users, set_users] = useState([])
  const [shared_with, set_shared_with] = useState([])

  const keycloak = useKeycloak()
  const current_user_id = keycloak.tokenParsed.sub

  useEffect(() => {
    axios
      .get(`/api/user/all`)
      .then(({ data }) => {
        set_users(data)
      })
      .catch((error) => {
        console.log(error)
      })
  }, [current_user_id])

  useEffect(() => {
    if (session_id === null || session_id === undefined) {
      set_shared_with([])
      return
    }
    axios
      .get(`/api/session/${session_id}/shared_with`)
      .then((response) => {
        set_shared_with(response.data)
      })
      .catch((error) => {
        console.log(error)
      })
  }, [session_id])

  return (
    <TextField
      select
      label="Shared With"
      fullWidth
      value={shared_with}
      variant="standard"
      SelectProps={{
        multiple: true,
      }}
      sx={{ minWidth: "120px" }}
      onChange={async (e) => {
        HandleFail(async () => {
          const user_ids = e.target.value
          await axios.put(`/api/session/${session_id}/share`, {
            user_ids,
          })
          set_shared_with(user_ids)
          toast.success("Shared with updated")
        })
      }}
    >
      {users.map(({ id, username }) => (
        <MenuItem key={id} value={id}>
          {username}
        </MenuItem>
      ))}
    </TextField>
  )
}

function CreateSession({ set_sessions }) {
  const initial_form_data = {
    name: "",
    description: "",
    buffer_km: 0,
    region: "",
    region_type: "",
  }

  const [form_data, set_form_data] = useState(initial_form_data)
  const [open, set_open] = useState(false)
  const [study_area_options, set_study_area_options] = useState([])

  useEffect(() => {
    if (!form_data.region_type) {
      return
    }

    axios
      .get(`/api/data/options/${form_data.region_type}/`)
      .then(({ data }) => {
        set_study_area_options(data)
      })
  }, [form_data.region_type])

  const handle_change = (event) => {
    const name = event.target.name
    var new_value = event.target.value

    var new_state = {
      ...form_data,
      [name]: new_value,
    }
    if (name === "region_type") {
      new_state.region = ""
    }
    set_form_data(new_state)
  }

  const handle_close = () => {
    set_form_data(initial_form_data)
    set_open(false)
  }

  const create_session = async () => {
    HandleFail(async () => {
      if (!form_data.name) {
        throw new Error("A name is required")
      }
      if (!form_data.description) {
        throw new Error("A description is required")
      }
      if (!form_data.region_type) {
        throw new Error("A geography is required")
      }
      if (
        form_data.region === undefined ||
        form_data.region === null ||
        form_data.region === ""
      ) {
        throw new Error("A region is required")
      }
      const { data } = await axios.post("/api/session", form_data)
      set_sessions((sessions) => [...sessions, data])
      toast.success("Session created")
      handle_close()
    })
  }

  return (
    <Fragment>
      <Dialog open={open} onClose={handle_close}>
        <DialogContent>
          <TextField
            onChange={handle_change}
            value={form_data.name}
            name="name"
            label="Session Name"
            fullWidth
            margin="dense"
            variant="standard"
          />
          <TextField
            onChange={handle_change}
            value={form_data.description}
            name="description"
            label="Session Description"
            fullWidth
            margin="dense"
            variant="standard"
          />

          <TextField
            value={form_data.region_type}
            fullWidth
            name="region_type"
            label="Geography"
            variant="standard"
            select
            onChange={handle_change}
          >
            <MenuItem value="country">Country</MenuItem>
            <MenuItem value="province">Province</MenuItem>
            <MenuItem value="district">District</MenuItem>
          </TextField>
          <TextField
            margin="dense"
            select
            label={
              form_data.region_type === "district"
                ? "District Municipality"
                : form_data.region_type === "province"
                  ? "Province"
                  : "Country"
            }
            value={form_data.region}
            variant="standard"
            fullWidth
            name="region"
            onChange={handle_change}
          >
            {study_area_options
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
          </TextField>
          <TextField
            margin="dense"
            type="number"
            label={"Buffer Size (km)"}
            value={form_data.buffer_km}
            variant="standard"
            name="buffer_km"
            fullWidth
            onChange={handle_change}
          />
          <DialogActions>
            <Button onClick={handle_close}>Cancel</Button>
            <Button onClick={create_session} color="primary">
              Create
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        padding={1}
      >
        <Button
          variant="outlined"
          onClick={() => {
            set_open(true)
          }}
        >
          New Session
        </Button>
      </Box>
    </Fragment>
  )
}

function DeleteSession({ session, set_session, set_sessions }) {
  const delete_session = async (id) => {
    HandleFail(async () => {
      const response = await axios.delete(`/api/session/${id}`)
      set_sessions((sessions) => {
        let updated_sessions = [...sessions]
        updated_sessions = updated_sessions.filter(
          (session) => session.id !== response.data.id,
        )
        return updated_sessions
      })
      set_session(null)
      toast.success("Session deleted")
    })
  }
  return (
    <Dialog
      open={session !== null}
      onClose={() => {
        set_session(null)
      }}
    >
      <DialogContent>
        <Typography>
          Are you sure you want to delete the session "{session?.name}"?
        </Typography>
        <Typography color="red">This action cannot be undone.</Typography>
        <Typography variant="caption">
          Note: Only session without submitted changes can be deleted.
        </Typography>
        <DialogActions>
          <Button
            onClick={() => {
              set_session(null)
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            onClick={() => {
              delete_session(session.id)
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  )
}

function SessionsTable() {
  const [sessions, set_sessions] = useState([])
  const [delete_session, set_delete_session] = useState(null)

  useEffect(() => {
    axios
      .get("/api/session/all")
      .then((response) => {
        set_sessions(response.data)
      })
      .catch((error) => {
        console.log(error)
      })
  }, [])

  return (
    <Fragment>
      <TableContainer elevation={3}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Open</TableCell>
              <TableCell>Share</TableCell>
              <TableCell>Delete</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sessions.map((session) => (
              <TableRow key={session.id} hover>
                <TableCell>{session.name}</TableCell>
                <TableCell>{session.description}</TableCell>
                <TableCell>
                  {new Date(session.create_time).toLocaleDateString()}
                </TableCell>
                <TableCell>{session.owner?.username}</TableCell>
                <TableCell>
                  <IconButton component={Link} to={`/session/${session.id}`}>
                    <MapIcon />
                  </IconButton>
                </TableCell>
                <TableCell>
                  <SharedWith session_id={session.id} />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => set_delete_session(session)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CreateSession set_sessions={set_sessions} />
      <DeleteSession
        session={delete_session}
        set_session={set_delete_session}
        set_sessions={set_sessions}
      />
    </Fragment>
  )
}

function SessionManagement() {
  return (
    <Fragment>
      <Header />
      <Box display="flex" justifyContent="center">
        <Paper sx={{ marginTop: 10, padding: 3 }}>
          <Typography variant="h6" noWrap>
            Session Management
          </Typography>
          <SessionsTable />
        </Paper>
      </Box>
    </Fragment>
  )
}

export default SessionManagement
